/**
 * Stockroom Brand Story — Instagram 1:1 carousel (5 slides).
 * High-retention visual system: asymmetric compositions, graphic metaphors,
 * and Blank Canvas brand colours (black / #00E676 / white).
 */

export const BRAND_STORY_SLIDE_COUNT = 5;

export const BRAND_STORY_SLIDES = [
  { id: 'open', label: '01 // OPEN', title: 'Wordmark' },
  { id: 'tension', label: '02 // TENSION', title: 'The Problem' },
  { id: 'promise', label: '03 // PROMISE', title: 'The Stockroom' },
  { id: 'how', label: '04 // HOW', title: 'How It Works' },
  { id: 'close', label: '05 // CLOSE', title: 'CTA' },
];

export const BRAND_STORY_CAPTION = `Too many tabs. Too much noise.

Stockroom is a fashion marketplace for premium brands at unexpected prices — authorised retailers, live deals, and a watchlist that gets you to checkout without the clutter.

Find it before it sells out.

Link in bio.`;

const SHARED_CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  html, body {
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #000;
  }

  body {
    font-family: 'Syne', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    color: #fff;
  }

  .slide {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #000;
  }

  /* Atmosphere layers */
  .grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px);
    background-size: 54px 54px;
    mask-image: radial-gradient(ellipse at 50% 40%, #000 20%, transparent 75%);
    -webkit-mask-image: radial-gradient(ellipse at 50% 40%, #000 20%, transparent 75%);
    pointer-events: none;
    z-index: 0;
  }

  .orb {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    z-index: 0;
    filter: blur(2px);
  }

  .orb--a {
    width: 70%;
    height: 70%;
    top: -18%;
    left: -22%;
    background: radial-gradient(circle, rgba(0,230,118,0.22) 0%, transparent 68%);
  }

  .orb--b {
    width: 55%;
    height: 55%;
    bottom: -20%;
    right: -15%;
    background: radial-gradient(circle, rgba(0,230,118,0.12) 0%, transparent 70%);
  }

  .orb--hot {
    width: 90%;
    height: 90%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: radial-gradient(circle, rgba(0,230,118,0.28) 0%, rgba(0,230,118,0.06) 35%, transparent 65%);
  }

  .noise {
    position: absolute;
    inset: 0;
    opacity: 0.04;
    pointer-events: none;
    z-index: 2;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  }

  .frame {
    position: absolute;
    inset: 4.2%;
    border: 1px solid rgba(255,255,255,0.1);
    pointer-events: none;
    z-index: 3;
  }

  .frame::before,
  .frame::after {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    border-color: #00e676;
    border-style: solid;
  }

  .frame::before {
    top: -1px;
    left: -1px;
    border-width: 2px 0 0 2px;
  }

  .frame::after {
    bottom: -1px;
    right: -1px;
    border-width: 0 2px 2px 0;
  }

  .corner-tr,
  .corner-bl {
    position: absolute;
    width: 18px;
    height: 18px;
    border-color: #00e676;
    border-style: solid;
    z-index: 4;
    pointer-events: none;
  }

  .corner-tr {
    top: 4.2%;
    right: 4.2%;
    border-width: 2px 2px 0 0;
    margin-top: -1px;
    margin-right: -1px;
  }

  .corner-bl {
    bottom: 4.2%;
    left: 4.2%;
    border-width: 0 0 2px 2px;
    margin-bottom: -1px;
    margin-left: -1px;
  }

  .meta {
    position: absolute;
    z-index: 5;
    font-family: 'IBM Plex Mono', 'Courier New', monospace;
    font-size: clamp(0.55rem, 1.7vmin, 0.85rem);
    font-weight: 700;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.45);
  }

  .meta--tl { top: 6.5%; left: 7%; }
  .meta--tr { top: 6.5%; right: 7%; color: #00e676; }
  .meta--bl { bottom: 6.5%; left: 7%; }
  .meta--br { bottom: 6.5%; right: 7%; }

  .content {
    position: relative;
    z-index: 5;
    width: 100%;
    height: 100%;
    padding: 14% 9%;
    display: flex;
    flex-direction: column;
  }

  .stock { color: #00e676; }
  .room { color: #fff; }

  /* ── Slide 01: Open ── */
  .open .content {
    align-items: flex-start;
    justify-content: flex-end;
    padding-bottom: 16%;
  }

  .open .wordmark {
    font-family: 'Bebas Neue', 'Impact', sans-serif;
    font-size: clamp(5.5rem, 28vmin, 15rem);
    font-weight: 400;
    letter-spacing: -0.02em;
    line-height: 0.82;
    mix-blend-mode: normal;
  }

  .open .slash {
    position: absolute;
    top: 18%;
    right: 8%;
    width: 2px;
    height: 42%;
    background: linear-gradient(to bottom, transparent, #00e676, transparent);
    transform: rotate(18deg);
    z-index: 4;
  }

  .open .tag {
    margin-top: 4%;
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(0.7rem, 2.2vmin, 1.05rem);
    letter-spacing: 0.35em;
    color: rgba(255,255,255,0.55);
  }

  .open .green-bar {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 1.4%;
    background: #00e676;
    z-index: 6;
  }

  /* ── Slide 02: Tension ── */
  .tension .content {
    justify-content: center;
    gap: 5%;
  }

  .tension .eyebrow {
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(0.65rem, 2vmin, 0.95rem);
    letter-spacing: 0.32em;
    color: #00e676;
  }

  .tension .headline {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(3.2rem, 14vmin, 7.5rem);
    line-height: 0.92;
    letter-spacing: -0.01em;
  }

  .tension .headline em {
    font-style: normal;
    color: rgba(255,255,255,0.28);
    text-decoration: line-through;
    text-decoration-thickness: 0.06em;
  }

  .tabs {
    position: absolute;
    inset: 12% 6% auto 6%;
    display: flex;
    gap: 1.5%;
    z-index: 1;
    opacity: 0.55;
  }

  .tab {
    flex: 1;
    height: clamp(42px, 8vmin, 72px);
    border: 1px solid rgba(255,255,255,0.18);
    border-bottom: none;
    border-radius: 10px 10px 0 0;
    background: rgba(255,255,255,0.03);
    position: relative;
    overflow: hidden;
  }

  .tab::after {
    content: '';
    position: absolute;
    top: 35%;
    left: 12%;
    right: 28%;
    height: 8%;
    background: rgba(255,255,255,0.2);
  }

  .tab.is-dead {
    opacity: 0.35;
    transform: translateY(8px) rotate(-1.5deg);
  }

  .tab.is-dead::before {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      -32deg,
      transparent,
      transparent 6px,
      rgba(255,80,80,0.25) 6px,
      rgba(255,80,80,0.25) 7px
    );
  }

  .tab.is-live {
    border-color: rgba(0,230,118,0.55);
    background: rgba(0,230,118,0.08);
  }

  .chaos-x {
    position: absolute;
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(8rem, 40vmin, 22rem);
    color: rgba(255,255,255,0.04);
    line-height: 1;
    right: -4%;
    bottom: -8%;
    z-index: 0;
    letter-spacing: -0.06em;
    user-select: none;
  }

  /* ── Slide 03: Promise ── */
  .promise {
    background: #000;
  }

  .promise .split {
    position: absolute;
    inset: 0;
    display: grid;
    grid-template-columns: 1fr 1.15fr;
    z-index: 1;
  }

  .promise .panel-green {
    background: #00e676;
    clip-path: polygon(0 0, 100% 0, 82% 100%, 0 100%);
    display: flex;
    align-items: flex-end;
    padding: 12% 10% 14% 10%;
  }

  .promise .panel-green .vert {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(0.7rem, 2.1vmin, 1rem);
    font-weight: 700;
    letter-spacing: 0.4em;
    color: #003d24;
  }

  .promise .panel-dark {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 12% 10% 12% 6%;
    gap: 3%;
  }

  .promise .eyebrow {
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(0.6rem, 1.9vmin, 0.9rem);
    letter-spacing: 0.28em;
    color: #00e676;
  }

  .promise .headline {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(3rem, 13vmin, 7rem);
    line-height: 0.9;
    letter-spacing: -0.015em;
  }

  .promise .headline .accent {
    color: #00e676;
  }

  .promise .underline {
    width: 28%;
    height: 4px;
    background: #00e676;
    margin-top: 2%;
  }

  /* ── Slide 04: How ── */
  .how .content {
    justify-content: center;
    gap: 4%;
    padding-top: 16%;
  }

  .how .eyebrow {
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(0.65rem, 2vmin, 0.95rem);
    letter-spacing: 0.32em;
    color: #00e676;
    margin-bottom: 2%;
  }

  .steps {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 2.8%;
    width: 100%;
  }

  .steps li {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 4%;
    padding: 3.5% 0;
    border-top: 1px solid rgba(255,255,255,0.12);
  }

  .steps li:last-child {
    border-bottom: 1px solid rgba(255,255,255,0.12);
  }

  .steps .num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(2.2rem, 9vmin, 4.8rem);
    line-height: 1;
    color: #00e676;
    min-width: 1.2em;
  }

  .steps .label {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: clamp(1.15rem, 4.2vmin, 2.35rem);
    letter-spacing: -0.03em;
  }

  .steps .arrow {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(1.6rem, 5vmin, 2.8rem);
    color: rgba(0,230,118,0.7);
  }

  /* ── Slide 05: Close ── */
  .close .content {
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 3%;
  }

  .close .wordmark {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(4rem, 18vmin, 9.5rem);
    line-height: 0.85;
    letter-spacing: -0.02em;
  }

  .close .urgency {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: clamp(1.1rem, 4vmin, 2.1rem);
    letter-spacing: -0.02em;
    color: rgba(255,255,255,0.85);
    max-width: 80%;
  }

  .close .cta-block {
    margin-top: 4%;
    padding: 3.2% 7%;
    background: #00e676;
    color: #000;
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: clamp(0.85rem, 2.6vmin, 1.25rem);
    letter-spacing: 0.28em;
  }

  .close .pulse-ring {
    position: absolute;
    width: 58%;
    height: 58%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -52%);
    border: 1px solid rgba(0,230,118,0.35);
    border-radius: 50%;
    z-index: 1;
    pointer-events: none;
  }

  .close .pulse-ring::before {
    content: '';
    position: absolute;
    inset: 8%;
    border: 1px solid rgba(0,230,118,0.18);
    border-radius: 50%;
  }
`;

function wrapSlide(slideClass, bodyInner, meta = {}) {
  const index = meta.index ?? '01';
  const code = meta.code ?? 'OPEN';
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Stockroom Brand Story — ${code}</title>
  <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Syne:wght@700;800&family=IBM+Plex+Mono:wght@500;700&display=swap" rel="stylesheet" />
  <style>${SHARED_CSS}</style>
</head>
<body>
  <main class="slide ${slideClass}" role="img" aria-label="Stockroom brand story — ${code}">
    <div class="grid" aria-hidden="true"></div>
    ${meta.orbs || ''}
    <div class="noise" aria-hidden="true"></div>
    <div class="frame" aria-hidden="true"></div>
    <div class="corner-tr" aria-hidden="true"></div>
    <div class="corner-bl" aria-hidden="true"></div>
    <div class="meta meta--tl">STOCKROOM</div>
    <div class="meta meta--tr">${index} / 05</div>
    <div class="meta meta--bl">${code}</div>
    <div class="meta meta--br">IG · 1:1</div>
    ${bodyInner}
  </main>
</body>
</html>`;
}

/**
 * @param {number} slideIndex 0–4
 * @returns {string} full HTML document
 */
export function buildBrandStorySlideHTML(slideIndex = 0) {
  const i = Math.max(0, Math.min(BRAND_STORY_SLIDE_COUNT - 1, Number(slideIndex) || 0));

  switch (i) {
    case 0:
      return wrapSlide(
        'open',
        `
        <div class="orb orb--hot" aria-hidden="true"></div>
        <div class="slash" aria-hidden="true"></div>
        <div class="content">
          <h1 class="wordmark"><span class="stock">Stock</span><br /><span class="room">room</span></h1>
          <p class="tag">MARKETPLACE // AU</p>
        </div>
        <div class="green-bar" aria-hidden="true"></div>
        `,
        { index: '01', code: 'OPEN', orbs: '' },
      );

    case 1:
      return wrapSlide(
        'tension',
        `
        <div class="chaos-x" aria-hidden="true">NOISE</div>
        <div class="tabs" aria-hidden="true">
          <div class="tab is-dead"></div>
          <div class="tab is-dead"></div>
          <div class="tab is-dead"></div>
          <div class="tab is-live"></div>
          <div class="tab is-dead"></div>
        </div>
        <div class="orb orb--b" aria-hidden="true"></div>
        <div class="content">
          <div class="eyebrow">THE PROBLEM</div>
          <h1 class="headline">Too many<br /><em>tabs.</em><br />Too much<br />noise.</h1>
        </div>
        `,
        { index: '02', code: 'TENSION' },
      );

    case 2:
      return wrapSlide(
        'promise',
        `
        <div class="split">
          <div class="panel-green">
            <div class="vert">THE STOCKROOM</div>
          </div>
          <div class="panel-dark">
            <div class="eyebrow">THE PROMISE</div>
            <h1 class="headline">Premium<br />brands.<br /><span class="accent">Unexpected<br />prices.</span></h1>
            <div class="underline" aria-hidden="true"></div>
          </div>
        </div>
        `,
        { index: '03', code: 'PROMISE' },
      );

    case 3:
      return wrapSlide(
        'how',
        `
        <div class="orb orb--a" aria-hidden="true"></div>
        <div class="content">
          <div class="eyebrow">HOW IT WORKS</div>
          <ol class="steps">
            <li>
              <span class="num">01</span>
              <span class="label">Authorised retailers</span>
              <span class="arrow" aria-hidden="true">↓</span>
            </li>
            <li>
              <span class="num">02</span>
              <span class="label">Live deals</span>
              <span class="arrow" aria-hidden="true">↓</span>
            </li>
            <li>
              <span class="num">03</span>
              <span class="label">Watchlist → Checkout</span>
              <span class="arrow" aria-hidden="true">→</span>
            </li>
          </ol>
        </div>
        `,
        { index: '04', code: 'SYSTEM' },
      );

    case 4:
    default:
      return wrapSlide(
        'close',
        `
        <div class="pulse-ring" aria-hidden="true"></div>
        <div class="orb orb--hot" aria-hidden="true"></div>
        <div class="content">
          <h1 class="wordmark"><span class="stock">Stock</span><span class="room">room</span></h1>
          <p class="urgency">Find it before it sells out.</p>
          <div class="cta-block">LINK IN BIO</div>
        </div>
        `,
        { index: '05', code: 'CTA' },
      );
  }
}

export function buildAllBrandStorySlideHTMLs() {
  return Array.from({ length: BRAND_STORY_SLIDE_COUNT }, (_, i) => buildBrandStorySlideHTML(i));
}
